import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MapGL, { Source, Layer, Marker, ScaleControl } from "react-map-gl";
import type { MapLayerMouseEvent, MapRef, GeoJSONSource } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { env } from "@/config/env";
import { useMapStore } from "@/stores/mapStore";
import { useMarketStore } from "@/stores/marketStore";
import { useChatStore } from "@/stores/chatStore";
import { useToastStore } from "@/stores/toastStore";
import { MARKETS } from "@/config/markets";
import { MapPopup } from "./MapPopup";
import { useMapDraw } from "@/features/map/hooks/useMapDraw";
import { reverseGeocode } from "@/services/geocode";
import { fetchPolygon } from "@/services/api";
import { addStreetsSource, removeStreetsSource } from "@/features/map/utils/mapContext";
import type { CircleLayer, FillLayer, LineLayer, SymbolLayer } from "react-map-gl";

// ── Map styles ──
const MAP_STYLE = "mapbox://styles/combify/cmndew77a000101qx4y7cclc1";
const SATELLITE_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";

// ── Entity type color maps — warm clay for properties, teal family for activity types ──
const ENTITY_COLORS: Record<string, string> = {
  property: "#C08070",
  project: "#2A7A6E",
  permit: "#3A8F95",
  tender: "#245E6E",
  organization: "#4DA3A8",
  default: "#1F595E",
};

const ENTITY_COLORS_DARK: Record<string, string> = {
  property: "#D4968A",
  project: "#3AABB2",
  permit: "#5BC4CA",
  tender: "#3A8FA0",
  organization: "#6DC0C5",
  default: "#3AABB2",
};

const ENTITY_COLORS_SATELLITE: Record<string, string> = {
  property: "#F0DDD5",
  default: "#FFFFFF",
};

// ── Entity colors (Mapbox GL paint requires hardcoded hex — no CSS vars) ──
const ENTITY_COLOR = "#1F595E"; // Gran — light mode default
const ENTITY_COLOR_DARK = "#3AABB2"; // Lighter teal — dark mode (matches --color-accent-text dark)
const ENTITY_COLOR_SATELLITE = "#FFFFFF"; // White on satellite for contrast

// ── Mapbox match expression for per-entity-type colors ──
function entityColorExpr(theme: "light" | "dark" | "satellite"): mapboxgl.Expression {
  const map =
    theme === "satellite" ? ENTITY_COLORS_SATELLITE :
    theme === "dark" ? ENTITY_COLORS_DARK :
    ENTITY_COLORS;
  return [
    "match", ["get", "entity_type"],
    "property", map.property ?? map.default!,
    "project", map.project ?? map.default!,
    "permit", map.permit ?? map.default!,
    "tender", map.tender ?? map.default!,
    "organization", map.organization ?? map.default!,
    map.default!,
  ] as mapboxgl.Expression;
}

// ── Layer definitions (all use slot="middle" for Mapbox Standard) ──

const CLUSTER_LAYER: CircleLayer = {
  id: "clusters",
  type: "circle",
  source: "results-points",
  slot: "top",
  filter: ["has", "point_count"],
  paint: {
    "circle-color": "#1F595E",
    "circle-stroke-color": "#FFFFFF",
    "circle-stroke-width": 2,
    "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
    "circle-emissive-strength": 1,
    "circle-opacity-transition": { duration: 500, delay: 0 },
    "circle-stroke-opacity-transition": { duration: 500, delay: 0 },
  },
};

const CLUSTER_COUNT_LAYER: SymbolLayer = {
  id: "cluster-count",
  type: "symbol",
  source: "results-points",
  slot: "top",
  filter: ["has", "point_count"],
  layout: {
    "text-field": ["get", "point_count_abbreviated"],
    "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
    "text-size": 13,
  },
  paint: {
    "text-color": "#FFFFFF",
    "text-emissive-strength": 1,
    "text-opacity-transition": { duration: 500, delay: 0 },
  },
};

const UNCLUSTERED_POINT_LAYER: CircleLayer = {
  id: "unclustered-point",
  type: "circle",
  source: "results-points",
  slot: "top",
  filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "render_as"], "point"]],
  paint: {
    "circle-color": ENTITY_COLOR,
    "circle-radius": [
      "case",
      ["boolean", ["feature-state", "hover"], false], 8,
      5.5,
    ],
    "circle-stroke-color": "#FFFFFF",
    "circle-stroke-width": 1.5,
    "circle-opacity": [
      "case",
      ["boolean", ["feature-state", "hover"], false], 1,
      0.85,
    ],
    "circle-emissive-strength": 1,
    "circle-opacity-transition": { duration: 600, delay: 0 },
    "circle-stroke-opacity-transition": { duration: 600, delay: 0 },
  },
};

// ── Heatmap layer — density visualization at low zoom, crossfades into clusters ──

const HEATMAP_OPACITY_EXPR: mapboxgl.Expression = [
  "interpolate", ["linear"], ["zoom"],
  9, 0.8,
  12, 0.3,
  13, 0,
];

// ── Polygon paint expressions (extracted for fadeIn/fadeOut restore) ──
const POLYGON_FILL_OPACITY_EXPR: mapboxgl.Expression = [
  "interpolate", ["linear"], ["zoom"],
  9,  ["case",
        ["boolean", ["feature-state", "active"], false], 0.15,
        ["boolean", ["feature-state", "hover"], false], 0.08,
        0],
  12, ["case",
        ["boolean", ["feature-state", "active"], false], 0.22,
        ["boolean", ["feature-state", "hover"], false], 0.12,
        0.05],
  15, ["case",
        ["boolean", ["feature-state", "active"], false], 0.30,
        ["boolean", ["feature-state", "hover"], false], 0.15,
        0.08],
];

const POLYGON_LINE_OPACITY_EXPR: mapboxgl.Expression = [
  "interpolate", ["linear"], ["zoom"],
  11, 0,
  12, ["case",
        ["boolean", ["feature-state", "active"], false], 0.9,
        ["boolean", ["feature-state", "hover"], false], 0.6,
        0.30],
  15, ["case",
        ["boolean", ["feature-state", "active"], false], 0.9,
        ["boolean", ["feature-state", "hover"], false], 0.7,
        0.40],
];

// Polygon fill — invisible by default, fades in when feature-state "active" is set
// NOTE: zoom expressions must be top-level interpolate; case goes inside each stop
const POLYGON_FILL_LAYER: FillLayer = {
  id: "polygon-fill",
  type: "fill",
  source: "results-polygons",
  slot: "top",
  filter: ["==", ["get", "render_as"], "polygon"],
  minzoom: 9,
  paint: {
    "fill-color": ENTITY_COLOR,
    "fill-opacity": POLYGON_FILL_OPACITY_EXPR as unknown as number,
    "fill-emissive-strength": 1,
    "fill-opacity-transition": { duration: 250, delay: 0 },
  },
};

// Polygon outline — ambient at z12+, brightens on hover/active
const POLYGON_LINE_LAYER: LineLayer = {
  id: "polygon-line",
  type: "line",
  source: "results-polygons",
  slot: "top",
  filter: ["==", ["get", "render_as"], "polygon"],
  minzoom: 9,
  paint: {
    "line-color": ENTITY_COLOR,
    "line-width": [
      "interpolate", ["linear"], ["zoom"],
      9, ["case", ["boolean", ["feature-state", "active"], false], 1, 0.5],
      13, ["case", ["boolean", ["feature-state", "active"], false], 2.5, 1],
      16, ["case", ["boolean", ["feature-state", "active"], false], 3.5, 1.5],
    ],
    "line-opacity": POLYGON_LINE_OPACITY_EXPR as unknown as number,
    "line-emissive-strength": 1,
    "line-opacity-transition": { duration: 250, delay: 0 },
  },
};

// ── Isochrone layers — always visible, rendered beneath entity polygons ──

const ISOCHRONE_FILL_LAYER: FillLayer = {
  id: "isochrone-fill",
  type: "fill",
  source: "results-isochrones",
  slot: "middle",
  paint: {
    "fill-color": ENTITY_COLOR,
    "fill-opacity": [
      "interpolate", ["linear"], ["get", "contour_minutes"],
      1, 0.22,
      10, 0.18,
      30, 0.12,
      60, 0.08,
    ],
    "fill-emissive-strength": 1,
    "fill-opacity-transition": { duration: 300, delay: 0 },
  },
};

const ISOCHRONE_LINE_LAYER: LineLayer = {
  id: "isochrone-line",
  type: "line",
  source: "results-isochrones",
  slot: "middle",
  paint: {
    "line-color": ENTITY_COLOR,
    "line-width": ["interpolate", ["linear"], ["zoom"], 8, 1.5, 13, 2.5],
    "line-opacity": 0.7,
    "line-dasharray": [4, 3],
    "line-emissive-strength": 1,
    "line-opacity-transition": { duration: 300, delay: 0 },
  },
};

const INTERACTIVE_LAYERS = ["unclustered-point", "polygon-fill", "clusters"];

// ── Camera padding — asymmetric to keep features clear of map overlays ──
// Top-left: loading badge + result count; top-right: zoom controls
const CAMERA_PADDING = { top: 72, right: 56, bottom: 40, left: 48 };
const CAMERA_PADDING_TIGHT = { top: 56, right: 48, bottom: 32, left: 40 };

// ── Animation constants ──

const EASE_OUT_CUBIC = (t: number) => 1 - Math.pow(1 - t, 3);

// Original opacity expressions to restore after fade-in transitions
const POINT_OPACITY_EXPR: mapboxgl.Expression = [
  "case",
  ["boolean", ["feature-state", "hover"], false], 1,
  0.85,
];

/** Compute what fraction of `target` bounds is visible in `current` viewport */
function boundsOverlap(current: mapboxgl.LngLatBounds, target: mapboxgl.LngLatBounds): number {
  const intersectW = Math.max(0,
    Math.min(current.getEast(), target.getEast()) - Math.max(current.getWest(), target.getWest()));
  const intersectH = Math.max(0,
    Math.min(current.getNorth(), target.getNorth()) - Math.max(current.getSouth(), target.getSouth()));
  const targetArea = (target.getEast() - target.getWest()) * (target.getNorth() - target.getSouth());
  return targetArea > 0 ? (intersectW * intersectH) / targetArea : 0;
}

// ── Component ──

interface MapPanelProps {
  onAskAbout?: (entityType: string, entityId: string, name: string) => void;
}

export function MapPanel({ onAskAbout }: MapPanelProps) {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoveredFeatureRef = useRef<{ source: string; id: number } | null>(null);
  const animationRef = useRef<{ cancel: () => void } | null>(null);
  const isProgrammaticMoveRef = useRef(false);
  const highlightedBuildingsRef = useRef<Array<{ source: string; sourceLayer: string; id: string | number }>>([]);
  const fetchedPolygonsRef = useRef(new Set<string>());
  const [cursor, setCursor] = useState<string>("crosshair");
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState(MAP_STYLE);
  const isSatellite = mapStyle !== MAP_STYLE;
  const [entityColor, setEntityColor] = useState(ENTITY_COLOR);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [mapBearing, setMapBearing] = useState(0);
  const [mapPitch, setMapPitch] = useState(0);
  const [is3D, setIs3D] = useState(false);

  const { features, viewport, popupInfo, userHasInteracted, flyToTarget, clickedLocation, drawnPolygon, isDrawing, activePolygonIds, pinnedPolygonIds, setPopupInfo, setUserHasInteracted, setClickedLocation, findFeature, activatePolygon, deactivatePolygon, clearActivePolygons, clearIsochrones, setDrawnPolygon, setIsDrawing } = useMapStore();
  const mapIntent = useMapStore((s) => s.mapIntent);
  const stagedFeatures = useMapStore((s) => s.stagedFeatures);
  const revealedEntityIds = useMapStore((s) => s.revealedEntityIds);
  const showAllResults = useMapStore((s) => s.showAllResults);
  const backgroundFeatures = useMapStore((s) => s.backgroundFeatures);
  const coordFlyTarget = useMapStore((s) => s.coordFlyTarget);
  const setCoordFlyTarget = useMapStore((s) => s.setCoordFlyTarget);
  const { market } = useMarketStore();
  const { isStreaming } = useChatStore();
  const { addToast } = useToastStore();
  const { i18n } = useTranslation();
  const marketConfig = MARKETS[market];

  // ── Initial lightPreset from saved theme (no flash on load) ──
  const initialLightPreset = useMemo(() => {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem("ortvis-theme") : null;
    return saved === "dark" ? "dusk" : "day";
  }, []);

  // ── Initial viewport ──
  const initialViewState = useMemo(
    () => ({
      longitude: marketConfig.defaultCenter[0],
      latitude: marketConfig.defaultCenter[1],
      zoom: marketConfig.defaultZoom,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [market],
  );

  // ── Drawing hook ──
  const handleDrawComplete = useCallback((polygon: { coordinates: [number, number][]; name: string }) => {
    setDrawnPolygon(polygon);
    setUserHasInteracted(true); // User is already viewing the area — skip camera animation on results
  }, [setDrawnPolygon, setUserHasInteracted]);

  const toggleMapStyle = useCallback(() => {
    setMapStyle((prev) => prev === MAP_STYLE ? SATELLITE_STYLE : MAP_STYLE);
  }, []);

  const handleZoomIn = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    map?.zoomIn({ duration: 300 });
  }, []);

  const handleZoomOut = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    map?.zoomOut({ duration: 300 });
  }, []);

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) {
      addToast({ type: "error", message: t("map.geolocateError") });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: [number, number] = [pos.coords.longitude, pos.coords.latitude];
        setUserLocation(loc);
        const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
        if (map) {
          isProgrammaticMoveRef.current = true;
          map.flyTo({ center: loc, zoom: 15, duration: 1200, easing: EASE_OUT_CUBIC });
        }
      },
      () => {
        addToast({ type: "error", message: t("map.geolocateError") });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [addToast, t]);

  const handleToggle3D = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    const currentPitch = map.getPitch();
    isProgrammaticMoveRef.current = true;
    if (currentPitch < 10) {
      setIs3D(true);
      map.easeTo({ pitch: 60, duration: 800, easing: EASE_OUT_CUBIC });
    } else {
      setIs3D(false);
      map.easeTo({ pitch: 0, bearing: 0, duration: 800, easing: EASE_OUT_CUBIC });
    }
  }, []);

  const handleResetNorth = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    isProgrammaticMoveRef.current = true;
    setIs3D(false);
    map.easeTo({ bearing: 0, pitch: 0, duration: 600, easing: EASE_OUT_CUBIC });
  }, []);

  const handleFitToResults = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map || !features) return;
    const boundsFeatures = features.features.filter((f) => f.properties?.render_as !== "isochrone");
    if (boundsFeatures.length === 0) return;
    const bounds = new mapboxgl.LngLatBounds();
    for (const feature of boundsFeatures) {
      const geom = feature.geometry;
      if (geom.type === "Point") bounds.extend(geom.coordinates as [number, number]);
      else if (geom.type === "Polygon") for (const c of geom.coordinates[0]!) bounds.extend(c as [number, number]);
      else if (geom.type === "MultiPolygon") for (const r of geom.coordinates) for (const c of r[0]!) bounds.extend(c as [number, number]);
    }
    if (bounds.isEmpty()) return;
    isProgrammaticMoveRef.current = true;
    setUserHasInteracted(false);
    map.fitBounds(bounds, { padding: CAMERA_PADDING, maxZoom: 15, duration: 1000, easing: EASE_OUT_CUBIC });
  }, [features, setUserHasInteracted]);

  const handleDrawCancel = useCallback(() => {
    setIsDrawing(false);
  }, [setIsDrawing]);

  useMapDraw(mapRef, isDrawing, handleDrawComplete, handleDrawCancel, i18n.language, entityColor);

  // ── Effective features: bridges staged features into the rendering pipeline ──
  const effectiveFeatures = useMemo(() => {
    // After analysis turn completes: merge committed features with background (tagged _revealed: 0)
    if (!mapIntent && backgroundFeatures) {
      const committed = (features?.features ?? []).map((f) => ({
        ...f,
        properties: { ...f.properties, _revealed: 1 },
      }));
      const bg = backgroundFeatures.features.map((f) => ({
        ...f,
        properties: { ...f.properties, _revealed: 0 },
      }));
      return { type: "FeatureCollection" as const, features: [...committed, ...bg] };
    }

    if (!mapIntent) return features;

    if (mapIntent.mode === "analysis" && mapIntent.transition === "focus") {
      // Focus mode: tag existing features with revealed state
      const existingFeatures = (features?.features ?? []).map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          _revealed: revealedEntityIds.has(f.properties?.entity_id) ? 1 : 0,
        },
      }));
      return { type: "FeatureCollection" as const, features: existingFeatures };
    }

    if (mapIntent.mode !== "analysis" || !stagedFeatures) {
      return features;
    }

    // Analysis mode (non-focus): staged features + prior committed features
    const tagged = stagedFeatures.features.map((f) => {
      const entityId = f.properties?.entity_id;
      const isRevealed = entityId && revealedEntityIds.has(entityId);
      return {
        ...f,
        properties: { ...f.properties, _revealed: isRevealed ? 1 : 0 },
      };
    });

    const priorFeatures = (features?.features ?? []).map((f) => ({
      ...f,
      properties: { ...f.properties, _revealed: 1 },
    }));

    return {
      type: "FeatureCollection" as const,
      features: [...priorFeatures, ...tagged],
    };
  }, [features, mapIntent, stagedFeatures, revealedEntityIds, backgroundFeatures]);

  // ── Split features into points, polygons, and isochrones ──
  const splitFeatures = useMemo(() => {
    const all = effectiveFeatures?.features ?? [];
    const isochrones = all.filter((f) => f.properties?.render_as === "isochrone");
    const rest = all.filter((f) => f.properties?.render_as !== "isochrone");
    const points = rest.filter((f) => f.properties?.render_as === "point" || f.geometry.type === "Point");
    const polygons = rest.filter((f) => f.properties?.render_as === "polygon" || f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon");
    return { points, polygons, isochrones };
  }, [effectiveFeatures]);

  // ── Completed drawn polygon as GeoJSON for rendering ──
  const drawnPolygonFC: GeoJSON.FeatureCollection | null = useMemo(() => {
    if (!drawnPolygon || isDrawing || drawnPolygon.coordinates.length < 3) return null;
    const ring = [...drawnPolygon.coordinates, drawnPolygon.coordinates[0]!];
    return {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [ring] },
        properties: {},
      }],
    };
  }, [drawnPolygon, isDrawing]);

  // ── Theme-aware heatmap gradient (used in both JSX and imperative setPaintProperty) ──
  const heatmapGradient = useMemo(() => {
    const isDark = entityColor === ENTITY_COLOR_DARK;
    return isDark
      ? [
          "interpolate", ["linear"], ["heatmap-density"],
          0,    "rgba(0,0,0,0)",
          0.1,  "rgba(58,171,178,0.10)",
          0.25, "rgba(58,171,178,0.22)",
          0.4,  "rgba(58,171,178,0.36)",
          0.6,  "rgba(58,171,178,0.48)",
          0.8,  "rgba(58,171,178,0.60)",
          1,    "rgba(58,171,178,0.75)",
        ]
      : [
          "interpolate", ["linear"], ["heatmap-density"],
          0,    "rgba(0,0,0,0)",
          0.1,  "rgba(31,89,94,0.06)",
          0.25, "rgba(31,89,94,0.16)",
          0.4,  "rgba(31,89,94,0.30)",
          0.6,  "rgba(31,89,94,0.42)",
          0.8,  "rgba(31,89,94,0.56)",
          1,    "rgba(31,89,94,0.72)",
        ];
  }, [entityColor]);

  // ── Staged rendering for smooth transitions ──
  const emptyFC: GeoJSON.FeatureCollection = useMemo(() => ({ type: "FeatureCollection", features: [] }), []);
  const [renderedPoints, setRenderedPoints] = useState<GeoJSON.FeatureCollection>(emptyFC);
  const [renderedPolygons, setRenderedPolygons] = useState<GeoJSON.FeatureCollection>(emptyFC);
  const [renderedIsochrones, setRenderedIsochrones] = useState<GeoJSON.FeatureCollection>(emptyFC);
  const prevFeaturesRef = useRef<GeoJSON.FeatureCollection | null>(null);

  // ── Analysis mode: sync staged features directly into rendered state ──
  useEffect(() => {
    if (mapIntent?.mode !== "analysis") return;
    const { points, polygons, isochrones } = splitFeatures;
    setRenderedPoints({ type: "FeatureCollection", features: points });
    setRenderedPolygons({ type: "FeatureCollection", features: polygons });
    setRenderedIsochrones({ type: "FeatureCollection", features: isochrones });
  }, [mapIntent, splitFeatures]);

  // ── Entity ID → Mapbox numeric ID lookups ──
  const polygonIdMap = useMemo(() => {
    const lookup = new Map<string, number[]>();
    renderedPolygons.features.forEach((f, index) => {
      const eid = (f as GeoJSON.Feature).properties?.entity_id as string;
      if (eid) lookup.set(eid, [...(lookup.get(eid) ?? []), index]);
    });
    return lookup;
  }, [renderedPolygons]);

  const pointIdMap = useMemo(() => {
    const lookup = new Map<string, number>();
    renderedPoints.features.forEach((f, index) => {
      const eid = (f as GeoJSON.Feature).properties?.entity_id as string;
      if (eid && !lookup.has(eid)) lookup.set(eid, index);
    });
    return lookup;
  }, [renderedPoints]);

  // ── Sync active/pinned polygon IDs → Mapbox feature-state ──
  const prevActiveRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const map = mapRef.current?.getMap?.() ?? mapRef.current;
    if (!map || !renderedPolygons.features.length || !map.getSource("results-polygons")) return;

    // Clear previously active features
    for (const id of prevActiveRef.current) {
      try {
        map.setFeatureState({ source: "results-polygons", id }, { active: false });
      } catch { /* source may have been removed */ }
    }

    // Collect all entity IDs that should be active
    const allActiveIds = new Set([...activePolygonIds, ...pinnedPolygonIds]);
    const newActiveMapboxIds = new Set<number>();

    for (const entityId of allActiveIds) {
      const mapboxIds = polygonIdMap.get(entityId);
      if (mapboxIds) {
        for (const id of mapboxIds) {
          try {
            map.setFeatureState({ source: "results-polygons", id }, { active: true });
            newActiveMapboxIds.add(id);
          } catch { /* source may have been removed */ }
        }
      }
    }

    prevActiveRef.current = newActiveMapboxIds;
  }, [activePolygonIds, pinnedPolygonIds, polygonIdMap, renderedPolygons]);

  // ── Chat → Map cross-highlighting: hovering EntityChip highlights map feature ──
  const hoveredEntityId = useMapStore((s) => s.hoveredEntityId);
  const prevCrossHoverRef = useRef<{ pointIds: number[]; polyIds: number[] }>({ pointIds: [], polyIds: [] });

  useEffect(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    // Clear previous
    const prev = prevCrossHoverRef.current;
    try {
      for (const id of prev.pointIds) map.setFeatureState({ source: "results-points", id }, { hover: false });
      for (const id of prev.polyIds) map.setFeatureState({ source: "results-polygons", id }, { hover: false });
    } catch {}
    // Set new
    if (hoveredEntityId) {
      const pId = pointIdMap.get(hoveredEntityId);
      const polyIds = polygonIdMap.get(hoveredEntityId) ?? [];
      try {
        if (pId != null) map.setFeatureState({ source: "results-points", id: pId }, { hover: true });
        for (const id of polyIds) map.setFeatureState({ source: "results-polygons", id }, { hover: true });
      } catch {}
      prevCrossHoverRef.current = { pointIds: pId != null ? [pId] : [], polyIds: [...polyIds] };
    } else {
      prevCrossHoverRef.current = { pointIds: [], polyIds: [] };
    }
  }, [hoveredEntityId, pointIdMap, polygonIdMap]);

  // ── Helpers: fade layers in/out via paint property ──
  const getMap = useCallback(() =>
    (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.() ?? null, []);

  const fadeLayersOut = useCallback(() => {
    const map = getMap();
    if (!map) return;
    try {
      map.setPaintProperty("unclustered-point", "circle-opacity", 0);
      map.setPaintProperty("unclustered-point", "circle-stroke-opacity", 0);
      map.setPaintProperty("clusters", "circle-opacity", 0);
      map.setPaintProperty("clusters", "circle-stroke-opacity", 0);
      map.setPaintProperty("cluster-count", "text-opacity", 0);
      map.setPaintProperty("heatmap", "heatmap-opacity", 0);
      map.setPaintProperty("polygon-line", "line-opacity", 0);
      map.setPaintProperty("polygon-fill", "fill-opacity", 0);
    } catch { /* layers may not exist yet */ }
  }, [getMap]);

  const fadeLayersIn = useCallback(() => {
    const map = getMap();
    if (!map) return;
    try {
      // Heatmap blooms first (800ms transition via paint property)
      map.setPaintProperty("heatmap", "heatmap-opacity", HEATMAP_OPACITY_EXPR);
      // Clusters emerge 150ms later
      setTimeout(() => {
        const m = getMap(); if (!m) return;
        try {
          m.setPaintProperty("clusters", "circle-opacity", 1);
          m.setPaintProperty("clusters", "circle-stroke-opacity", 1);
          m.setPaintProperty("cluster-count", "text-opacity", 1);
        } catch {}
      }, 150);
      // Points + polygon outlines resolve 300ms later
      setTimeout(() => {
        const m = getMap(); if (!m) return;
        try {
          m.setPaintProperty("unclustered-point", "circle-opacity", POINT_OPACITY_EXPR);
          m.setPaintProperty("unclustered-point", "circle-stroke-opacity", 1);
          m.setPaintProperty("polygon-line", "line-opacity", POLYGON_LINE_OPACITY_EXPR);
          m.setPaintProperty("polygon-fill", "fill-opacity", POLYGON_FILL_OPACITY_EXPR);
        } catch {}
      }, 300);
    } catch {}
  }, [getMap]);

  // ── Focus mode flag ──
  const isFocusMode = mapIntent?.transition === "focus";

  // ── Analysis mode: update point layer opacity for reveal state ──
  useEffect(() => {
    const map = getMap();
    if (!map || !styleLoaded) return;

    if ((mapIntent?.mode === "analysis" || backgroundFeatures) && !isFocusMode) {
      // Set circle-opacity to depend on _revealed property
      map.setPaintProperty("unclustered-point", "circle-opacity", [
        "case",
        ["==", ["get", "_revealed"], 0],
        showAllResults ? 0.3 : 0,
        ["case",
          ["boolean", ["feature-state", "hover"], false], 1,
          0.85,
        ],
      ] as any);
      map.setPaintProperty("unclustered-point", "circle-stroke-opacity", [
        "case",
        ["==", ["get", "_revealed"], 0],
        showAllResults ? 0.2 : 0,
        1,
      ] as any);
      // Background dots are smaller
      map.setPaintProperty("unclustered-point", "circle-radius", [
        "case",
        ["==", ["get", "_revealed"], 0],
        showAllResults ? 4 : 0,
        ["case",
          ["boolean", ["feature-state", "hover"], false], 8,
          5.5,
        ],
      ] as any);
    } else if (!isFocusMode) {
      // Lookup mode or no intent — restore standard paint
      map.setPaintProperty("unclustered-point", "circle-opacity", [
        "case",
        ["boolean", ["feature-state", "hover"], false], 1,
        0.85,
      ] as any);
      map.setPaintProperty("unclustered-point", "circle-stroke-opacity", 1);
      map.setPaintProperty("unclustered-point", "circle-radius", [
        "case",
        ["boolean", ["feature-state", "hover"], false], 8,
        5.5,
      ] as any);
    }
  }, [mapIntent, isFocusMode, showAllResults, revealedEntityIds, backgroundFeatures, styleLoaded, getMap]);

  // ── Focus transition: dim others, highlight focused entity ──
  useEffect(() => {
    const map = getMap();
    if (!map || !styleLoaded || !isFocusMode) return;

    map.setPaintProperty("unclustered-point", "circle-opacity", [
      "case",
      ["==", ["get", "_revealed"], 1],
      1,
      0.3,
    ] as any);
    map.setPaintProperty("unclustered-point", "circle-radius", [
      "case",
      ["==", ["get", "_revealed"], 1],
      10,
      ["case",
        ["boolean", ["feature-state", "hover"], false], 8,
        5.5,
      ],
    ] as any);
    map.setPaintProperty("unclustered-point", "circle-stroke-opacity", [
      "case",
      ["==", ["get", "_revealed"], 1],
      1,
      0.2,
    ] as any);

    return () => {
      const m = getMap();
      if (!m) return;
      try {
        m.setPaintProperty("unclustered-point", "circle-opacity", [
          "case",
          ["boolean", ["feature-state", "hover"], false], 1,
          0.85,
        ] as any);
        m.setPaintProperty("unclustered-point", "circle-radius", [
          "case",
          ["boolean", ["feature-state", "hover"], false], 8,
          5.5,
        ] as any);
        m.setPaintProperty("unclustered-point", "circle-stroke-opacity", 1);
      } catch {}
    };
  }, [isFocusMode, revealedEntityIds, styleLoaded, getMap]);

  // ── Focus transition: ease camera toward focused entity ──
  useEffect(() => {
    if (!isFocusMode || revealedEntityIds.size === 0) return;
    const map = getMap();
    if (!map) return;

    const lastRevealed = Array.from(revealedEntityIds).pop();
    if (!lastRevealed) return;

    // Find in staged features first, then in committed features
    const store = useMapStore.getState();
    const allFeatures = [
      ...(store.stagedFeatures?.features ?? []),
      ...(store.features?.features ?? []),
    ];
    const feature = allFeatures.find(
      (f) => f.properties?.entity_id === lastRevealed && f.geometry.type === "Point"
    );
    if (!feature) return;

    const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
    isProgrammaticMoveRef.current = true;
    map.easeTo({
      center: coords,
      zoom: Math.max(map.getZoom(), 14),
      duration: 1000,
      easing: EASE_OUT_CUBIC,
    });
  }, [isFocusMode, revealedEntityIds, getMap]);

  // ── Analysis mode: fit camera to staged feature bounds ──
  useEffect(() => {
    if (mapIntent?.mode !== "analysis" || !stagedFeatures) return;
    const map = getMap();
    if (!map || stagedFeatures.features.length === 0) return;

    const boundsFeatures = stagedFeatures.features.filter(
      (f) => f.properties?.render_as !== "isochrone"
    );
    if (boundsFeatures.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    for (const f of boundsFeatures) {
      if (f.geometry.type === "Point") {
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        bounds.extend(coords);
      }
    }
    if (bounds.isEmpty()) return;

    isProgrammaticMoveRef.current = true;
    map.fitBounds(bounds, {
      padding: CAMERA_PADDING,
      maxZoom: 15,
      duration: 1400,
      easing: EASE_OUT_CUBIC,
    });
  }, [stagedFeatures, mapIntent, getMap]);

  // ── Building highlight helpers ──
  const clearBuildingHighlights = useCallback(() => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    for (const ref of highlightedBuildingsRef.current) {
      try {
        (map as unknown as { setFeatureState: (target: unknown, state: unknown) => void }).setFeatureState(
          { source: ref.source, sourceLayer: ref.sourceLayer, id: ref.id },
          { select: false },
        );
      } catch { /* */ }
    }
    highlightedBuildingsRef.current = [];
  }, []);

  const highlightBuildingsAt = useCallback((center: [number, number]) => {
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    clearBuildingHighlights();

    try {
      const point = map.project(center);
      const buffer = 60;
      const features = map.queryRenderedFeatures(
        [[point.x - buffer, point.y - buffer], [point.x + buffer, point.y + buffer]],
      );

      // Find building features — try multiple naming patterns used by Standard
      const buildings = features.filter((f) => {
        const sl = f.sourceLayer ?? "";
        const lid = f.layer?.id ?? "";
        return sl.includes("building") || lid.includes("building") || lid.includes("3d-building") || sl.includes("structure");
      });

      if (buildings.length === 0 && features.length > 0) {
        // Debug: log unique layer IDs to discover building layer names
        const layerIds = [...new Set(features.map((f) => `${f.layer?.id}|${f.sourceLayer}`))];
        console.debug("[Ortvis] No buildings found. Layers at point:", layerIds.slice(0, 15));
      }

      let highlighted = 0;
      for (const b of buildings) {
        if (b.id != null && b.source && b.sourceLayer) {
          try {
            (map as unknown as { setFeatureState: (target: unknown, state: unknown) => void }).setFeatureState(
              { source: b.source, sourceLayer: b.sourceLayer, id: b.id },
              { select: true },
            );
            highlightedBuildingsRef.current.push({ source: b.source, sourceLayer: b.sourceLayer, id: b.id });
            highlighted++;
          } catch { /* feature-state may not be supported */ }
        }
      }
      if (highlighted > 0) {
        console.debug(`[Ortvis] Highlighted ${highlighted} buildings at`, center);
      }
    } catch (err) {
      console.debug("[Ortvis] Building highlight query failed:", err);
    }
  }, [clearBuildingHighlights]);

  // ── Choreographed transition: fade-out → swap → fly → fade-in ──
  useEffect(() => {
    // Skip automatic animation in analysis mode — handled by staging/reveal effects
    if (mapIntent?.mode === "analysis" && stagedFeatures) return;

    const mapInstance = mapRef.current;
    if (!mapInstance) return;

    // Detect if this is a minor addition (polygon fetch) vs new search results
    const prevCount = prevFeaturesRef.current?.features.length ?? 0;
    const newCount = features?.features.length ?? 0;
    const isPolygonAddition = prevCount > 0 && newCount > 0 && newCount - prevCount <= 2 && newCount >= prevCount;

    if (isPolygonAddition) {
      // Just a polygon was added — update rendered data without animation or clearing state
      prevFeaturesRef.current = features;
      const all = features?.features ?? [];
      const points = all.filter((f) => f.properties?.render_as === "point" || f.geometry.type === "Point");
      const polygons = all.filter((f) => (f.properties?.render_as === "polygon" || f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon") && f.properties?.render_as !== "isochrone");
      const isochrones = all.filter((f) => f.properties?.render_as === "isochrone");
      setRenderedPoints({ type: "FeatureCollection", features: points });
      setRenderedPolygons({ type: "FeatureCollection", features: polygons });
      setRenderedIsochrones({ type: "FeatureCollection", features: isochrones });
      return;
    }

    // Cancel any in-flight animation
    animationRef.current?.cancel();

    // Clear active polygons, building highlights, and polygon fetch cache when new search results arrive
    clearActivePolygons();
    clearBuildingHighlights();
    fetchedPolygonsRef.current.clear();

    const hadPreviousFeatures = prevFeaturesRef.current && prevFeaturesRef.current.features.length > 0;
    prevFeaturesRef.current = features;

    // No features — fade out and clear
    if (!features || features.features.length === 0) {
      if (hadPreviousFeatures) {
        fadeLayersOut();
        const t = setTimeout(() => {
          setRenderedPoints(emptyFC);
          setRenderedPolygons(emptyFC);
          setRenderedIsochrones(emptyFC);
          fadeLayersIn();
        }, 300);
        animationRef.current = { cancel: () => clearTimeout(t) };
      } else {
        setRenderedPoints(emptyFC);
        setRenderedPolygons(emptyFC);
        setRenderedIsochrones(emptyFC);
      }
      return;
    }

    // Calculate new bounds (exclude isochrones — they're large context polygons
    // that would cause the camera to zoom out too far)
    const boundsFeatures = features.features.filter(
      (f) => f.properties?.render_as !== "isochrone",
    );
    const bounds = new mapboxgl.LngLatBounds();
    for (const feature of boundsFeatures) {
      const geom = feature.geometry;
      if (geom.type === "Point") {
        bounds.extend(geom.coordinates as [number, number]);
      } else if (geom.type === "Polygon") {
        for (const coord of geom.coordinates[0]!) {
          bounds.extend(coord as [number, number]);
        }
      } else if (geom.type === "MultiPolygon") {
        for (const ring of geom.coordinates) {
          for (const coord of ring[0]!) {
            bounds.extend(coord as [number, number]);
          }
        }
      }
    }

    const newPointFC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: splitFeatures.points };
    const newPolyFC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: splitFeatures.polygons };
    const newIsoFC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: splitFeatures.isochrones };

    // Resolve the raw map object for direct API calls
    const map = mapInstance.getMap?.() ?? mapInstance;

    if (hadPreviousFeatures && !userHasInteracted) {
      // ── PAGE-TURN TRANSITION ──
      // Determine how much the new features overlap the current viewport
      let cancelled = false;
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      const schedule = (fn: () => void, ms: number) => {
        const id = setTimeout(() => { if (!cancelled) fn(); }, ms);
        timeouts.push(id);
      };
      animationRef.current = {
        cancel: () => { cancelled = true; timeouts.forEach(clearTimeout); },
      };

      const currentBounds = map.getBounds?.();
      const overlap = (currentBounds && !bounds.isEmpty())
        ? boundsOverlap(currentBounds, bounds)
        : 0;

      if (overlap > 0.8) {
        // HIGH OVERLAP — crossfade in place, no camera move
        fadeLayersOut();
        schedule(() => {
          setRenderedPoints(newPointFC);
          setRenderedPolygons(newPolyFC);
          setRenderedIsochrones(newIsoFC);
        }, 300);
        schedule(() => fadeLayersIn(), 350);

      } else if (overlap > 0.3) {
        // PARTIAL OVERLAP — gentle shift with easeTo
        fadeLayersOut();
        schedule(() => {
          setRenderedPoints(newPointFC);
          setRenderedPolygons(newPolyFC);
          setRenderedIsochrones(newIsoFC);
          if (!bounds.isEmpty()) {
            isProgrammaticMoveRef.current = true;
            map.easeTo({
              ...map.cameraForBounds(bounds, { padding: CAMERA_PADDING, maxZoom: 15 }),
              duration: 1000,
              easing: EASE_OUT_CUBIC,
            });
          } else if (viewport) {
            isProgrammaticMoveRef.current = true;
            map.easeTo({ center: viewport.center, zoom: viewport.zoom, duration: 1000, easing: EASE_OUT_CUBIC });
          }
        }, 300);
        schedule(() => fadeLayersIn(), 900);

      } else {
        // LOW OVERLAP — full page-turn: fade out → swap + fly → fade in
        fadeLayersOut();
        schedule(() => {
          setRenderedPoints(newPointFC);
          setRenderedPolygons(newPolyFC);
          setRenderedIsochrones(newIsoFC);
          if (!bounds.isEmpty()) {
            isProgrammaticMoveRef.current = true;
            map.fitBounds(bounds, { padding: CAMERA_PADDING, maxZoom: 15, duration: 1400, easing: EASE_OUT_CUBIC });
          } else if (viewport) {
            // Bounds empty (e.g. isochrone-only results) — use viewport hint
            isProgrammaticMoveRef.current = true;
            map.flyTo({ center: viewport.center, zoom: viewport.zoom, duration: 1400, easing: EASE_OUT_CUBIC });
          }
        }, 350);
        schedule(() => fadeLayersIn(), 1050);
      }
    } else {
      // ── FIRST LOAD or user has interacted: set data immediately + fly ──
      setRenderedPoints(newPointFC);
      setRenderedPolygons(newPolyFC);
      setRenderedIsochrones(newIsoFC);

      if (!userHasInteracted && !bounds.isEmpty()) {
        // Use backend viewport hint if available, otherwise fit to bounds
        if (viewport) {
          isProgrammaticMoveRef.current = true;
          map.flyTo({ center: viewport.center, zoom: viewport.zoom, duration: 1200, easing: EASE_OUT_CUBIC });
        } else {
          isProgrammaticMoveRef.current = true;
          map.fitBounds(bounds, { padding: CAMERA_PADDING, maxZoom: 15, duration: 1200, easing: EASE_OUT_CUBIC });
        }
      }
    }
  }, [features, viewport, splitFeatures, userHasInteracted, emptyFC, clearActivePolygons, clearBuildingHighlights, fadeLayersOut, fadeLayersIn, mapIntent, stagedFeatures]);

  // ── Market switch: fly to new center ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    isProgrammaticMoveRef.current = true;
    map.flyTo({
      center: marketConfig.defaultCenter,
      zoom: marketConfig.defaultZoom,
      duration: 1500,
      easing: EASE_OUT_CUBIC,
    });
  }, [market, marketConfig]);

  // ── FlyTo entity from chat click ──
  // Helper: fly to entity, show popup, fetch polygon, highlight buildings
  const flyToEntity = useCallback((
    map: MapRef,
    entityId: string,
    entityType: string,
    coordinates: [number, number],
    name: string,
    municipality: string,
  ) => {
    isProgrammaticMoveRef.current = true;
    map.flyTo({ center: coordinates, zoom: 15, duration: 1200, easing: EASE_OUT_CUBIC });
    activatePolygon(entityId, false);

    // Fetch polygon on demand
    if (!polygonIdMap.has(entityId) &&
        entityType !== "organization" &&
        !fetchedPolygonsRef.current.has(entityId)) {
      fetchedPolygonsRef.current.add(entityId);
      const feat = useMapStore.getState().features?.features.find(
        (f) => f.properties?.entity_id === entityId,
      );
      const propertyId = entityType === "permit" ? (feat?.properties?.property_id as string | undefined) : undefined;
      fetchPolygon(entityType, entityId, propertyId).then((polygonFeature) => {
        if (!polygonFeature) return;
        const { features: current } = useMapStore.getState();
        if (!current) return;
        useMapStore.getState().setFeatures({
          type: "FeatureCollection",
          features: [...current.features, polygonFeature],
        });
        activatePolygon(entityId, false);
      });
    }

    setPopupInfo({ longitude: coordinates[0], latitude: coordinates[1], entityType, entityId, name, municipality });

    // Highlight buildings after camera settles
    const rawMap = (map as unknown as { getMap?: () => mapboxgl.Map }).getMap?.();
    if (rawMap) {
      const onIdle = () => {
        rawMap.off("idle", onIdle);
        setTimeout(() => highlightBuildingsAt(coordinates), 200);
      };
      rawMap.on("idle", onIdle);
    }
  }, [activatePolygon, setPopupInfo, highlightBuildingsAt, polygonIdMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyToTarget) return;

    const found = findFeature(flyToTarget.entityId);
    if (found) {
      // Reveal the entity if it's staged/background (makes it visible on map)
      useMapStore.getState().revealEntity(flyToTarget.entityId);
      flyToEntity(map, flyToTarget.entityId, found.entityType, found.coordinates, found.name, found.municipality);
      return;
    }

    // Entity not on the map — fetch its polygon to get geometry, add to map, then fly
    const entityType = flyToTarget.entityType ?? "property";
    if (!fetchedPolygonsRef.current.has(flyToTarget.entityId)) {
      fetchedPolygonsRef.current.add(flyToTarget.entityId);
      fetchPolygon(entityType, flyToTarget.entityId).then((polygonFeature) => {
        if (!polygonFeature?.geometry) return;
        const { features: current } = useMapStore.getState();
        // Add the fetched feature to the map
        const newFeatures = current
          ? [...current.features, polygonFeature]
          : [polygonFeature];
        useMapStore.getState().setFeatures({
          type: "FeatureCollection",
          features: newFeatures,
        });
        // Extract coordinates from the polygon centroid
        const geom = polygonFeature.geometry;
        let coords: [number, number];
        if (geom.type === "Point") {
          coords = geom.coordinates as [number, number];
        } else if (geom.type === "Polygon") {
          const ring = (geom as GeoJSON.Polygon).coordinates[0] as [number, number][];
          const lngs = ring.map((c) => c[0]);
          const lats = ring.map((c) => c[1]);
          coords = [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
        } else {
          return;
        }
        const name = (polygonFeature.properties?.name as string) ?? "";
        const municipality = (polygonFeature.properties?.municipality as string) ?? "";
        const m = mapRef.current;
        if (m) flyToEntity(m, flyToTarget.entityId, entityType, coords, name, municipality);
      });
    }
  }, [flyToTarget, findFeature, flyToEntity]);

  useEffect(() => {
    if (!coordFlyTarget) return;
    const map = getMap();
    if (!map) return;
    map.flyTo({
      center: [coordFlyTarget.longitude, coordFlyTarget.latitude],
      zoom: coordFlyTarget.zoom,
      duration: 1200,
      easing: EASE_OUT_CUBIC,
    });
    setCoordFlyTarget(null);
  }, [coordFlyTarget, getMap, setCoordFlyTarget]);

  // ── Escape key dismisses popup ──
  useEffect(() => {
    if (!popupInfo) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopupInfo(null);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [popupInfo, setPopupInfo]);

  // ── ResizeObserver ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      setTimeout(() => mapRef.current?.resize(), 50);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // ── Re-init styleLoaded on style switch (satellite ↔ standard) ──
  const prevMapStyleRef = useRef(mapStyle);
  useEffect(() => {
    if (prevMapStyleRef.current === mapStyle) return;
    prevMapStyleRef.current = mapStyle;
    // Unmount layers while new style loads
    setStyleLoaded(false);
    // Listen for the new style to finish loading
    const map = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
    if (!map) return;
    function onStyleLoad() {
      map!.off("style.load", onStyleLoad);
      setStyleLoaded(true);
      addStreetsSource(map!);
    }
    map.on("style.load", onStyleLoad);
    return () => {
      map.off("style.load", onStyleLoad);
      removeStreetsSource(map);
    };
  }, [mapStyle]);

  // ── Unified entity color + theme sync (satellite > dark > default) ──
  useEffect(() => {
    if (!styleLoaded) return;
    const getMapInst = () =>
      (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();

    const apply = () => {
      const map = getMapInst();
      if (!map) return;
      const isDark = document.documentElement.getAttribute("data-theme") === "dark";

      // Priority: satellite > dark > default
      let color: string;
      let stroke: string;
      if (isSatellite) {
        color = ENTITY_COLOR_SATELLITE;
        stroke = "rgba(0,0,0,0.4)";
      } else if (isDark) {
        color = ENTITY_COLOR_DARK;
        stroke = "#FFFFFF";
      } else {
        color = ENTITY_COLOR;
        stroke = "#FFFFFF";
      }

      // Keep React state in sync so JSX layers render with correct color
      setEntityColor(color);

      // Helper: setPaintProperty that skips missing layers (avoids console errors)
      const set = (layer: string, prop: string, value: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((map as any).getLayer(layer)) (map as any).setPaintProperty(layer, prop, value);
      };

      // Determine current theme for per-entity-type color expression
      const theme: "light" | "dark" | "satellite" = isSatellite ? "satellite" : isDark ? "dark" : "light";

      // Clusters (aggregate across entity types — single color)
      set("clusters", "circle-color", color);
      set("clusters", "circle-stroke-color", stroke);
      // Unclustered points: per-entity-type colors
      set("unclustered-point", "circle-color", entityColorExpr(theme));
      set("unclustered-point", "circle-stroke-color", entityColorExpr(theme));
      // Polygons
      set("polygon-fill", "fill-color", color);
      set("polygon-line", "line-color", color);
      // Isochrones (only present after travel-time query)
      set("isochrone-fill", "fill-color", color);
      set("isochrone-line", "line-color", color);
      // Drawn polygon (only present when user has drawn)
      set("drawn-polygon-fill", "fill-color", color);
      set("drawn-polygon-line", "line-color", color);
      // Draw-mode layers (managed by useMapDraw hook — may not exist)
      set("draw-fill", "fill-color", color);
      set("draw-line", "line-color", color);
      set("draw-vertices", "circle-color", color);

      // Heatmap gradient — brighter teal in dark mode for visibility on dark basemap
      const gradient: mapboxgl.Expression = isDark && !isSatellite
        ? [
            "interpolate", ["linear"], ["heatmap-density"],
            0,    "rgba(0,0,0,0)",
            0.1,  "rgba(58,171,178,0.10)",
            0.25, "rgba(58,171,178,0.22)",
            0.4,  "rgba(58,171,178,0.36)",
            0.6,  "rgba(58,171,178,0.48)",
            0.8,  "rgba(58,171,178,0.60)",
            1,    "rgba(58,171,178,0.75)",
          ]
        : [
            "interpolate", ["linear"], ["heatmap-density"],
            0,    "rgba(0,0,0,0)",
            0.1,  "rgba(31,89,94,0.06)",
            0.25, "rgba(31,89,94,0.16)",
            0.4,  "rgba(31,89,94,0.30)",
            0.6,  "rgba(31,89,94,0.42)",
            0.8,  "rgba(31,89,94,0.56)",
            1,    "rgba(31,89,94,0.72)",
          ];
      set("heatmap", "heatmap-color", gradient);

      // Basemap light preset + building highlights
      try {
        map.setConfigProperty("basemap", "lightPreset", isDark ? "dusk" : "day");
        const buildingColor = isSatellite ? ENTITY_COLOR : color;
        map.setConfigProperty("basemap", "colorBuildingHighlight", buildingColor);
        map.setConfigProperty("basemap", "colorBuildingSelect", buildingColor);
      } catch { /* satellite/non-Standard styles don't have basemap config */ }
    };
    apply();

    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, [isSatellite, styleLoaded]);

  // ── Helper: compute bounds of a polygon entity ──
  const getPolygonBounds = useCallback((entityId: string): mapboxgl.LngLatBounds | null => {
    const polygonFeatures = renderedPolygons.features.filter(
      (f) => (f as GeoJSON.Feature).properties?.entity_id === entityId,
    );
    if (polygonFeatures.length === 0) return null;

    const bounds = new mapboxgl.LngLatBounds();
    for (const f of polygonFeatures) {
      const geom = f.geometry;
      if (geom.type === "Polygon") {
        for (const coord of geom.coordinates[0]!) {
          bounds.extend(coord as [number, number]);
        }
      } else if (geom.type === "MultiPolygon") {
        for (const ring of geom.coordinates) {
          for (const coord of ring[0]!) {
            bounds.extend(coord as [number, number]);
          }
        }
      }
    }
    return bounds.isEmpty() ? null : bounds;
  }, [renderedPolygons]);

  // ── Click handler ──
  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = mapRef.current;
      if (!map) return;

      // Draw mode handles its own clicks
      if (isDrawing) return;

      const isShift = e.originalEvent.shiftKey;

      // If clicked a feature — handle it
      if (e.features?.length) {
        const feature = e.features[0]!;
        const layerId = feature.layer?.id;

        // Cluster click → expand
        if (layerId === "clusters") {
          const clusterId = feature.properties?.cluster_id as number;
          const source = map.getSource("results-points") as GeoJSONSource | undefined;
          if (source && feature.geometry.type === "Point") {
            const center = feature.geometry.coordinates as [number, number];
            (source as unknown as { getClusterExpansionZoom: (id: number, cb: (err: unknown, zoom: number) => void) => void })
              .getClusterExpansionZoom(clusterId, (_err: unknown, zoom: number) => {
                isProgrammaticMoveRef.current = true;
                map.flyTo({ center, zoom: zoom + 1, duration: 700, easing: EASE_OUT_CUBIC });
              });
          }
          return;
        }

        const props = feature.properties ?? {};
        const entityId = (props.entity_id as string) ?? "";
        // Toggle: clicking already-active polygon deactivates it
        const { activePolygonIds, pinnedPolygonIds } = useMapStore.getState();
        if (activePolygonIds.has(entityId) || pinnedPolygonIds.has(entityId)) {
          deactivatePolygon(entityId);
          setPopupInfo(null);
          return;
        }

        // Activate polygon for this entity
        activatePolygon(entityId, isShift);

        // Feature click → popup
        const geom = feature.geometry;
        let lng: number, lat: number;
        if (geom.type === "Point") {
          [lng, lat] = geom.coordinates as [number, number];
        } else {
          lng = e.lngLat.lng;
          lat = e.lngLat.lat;
        }

        setPopupInfo({
          longitude: lng,
          latitude: lat,
          entityType: (props.entity_type as string) ?? "property",
          entityId,
          name: (props.name as string) ?? "",
          municipality: (props.municipality as string) ?? "",
        });

        // Viewport fit or on-demand polygon fetch
        const entityType = (props.entity_type as string) ?? "property";
        const polygonBounds = getPolygonBounds(entityId);
        if (polygonBounds) {
          // Polygon already loaded — fit bounds if extends beyond view
          const mapBounds = map.getBounds();
          if (mapBounds &&
              (!mapBounds.contains(polygonBounds.getSouthWest()) ||
               !mapBounds.contains(polygonBounds.getNorthEast()))) {
            isProgrammaticMoveRef.current = true;
            map.fitBounds(polygonBounds, { padding: CAMERA_PADDING_TIGHT, maxZoom: 16, duration: 800, easing: EASE_OUT_CUBIC });
          } else if (map.getZoom() < 14) {
            isProgrammaticMoveRef.current = true;
            map.easeTo({ center: [lng, lat], zoom: 15, duration: 800, easing: EASE_OUT_CUBIC });
          }
        } else if (entityType !== "organization" &&
                   !fetchedPolygonsRef.current.has(entityId)) {
          // No polygon yet — fetch on demand (permits use linked property's polygon)
          fetchedPolygonsRef.current.add(entityId);
          const propertyId = entityType === "permit" ? (props.property_id as string) : undefined;
          fetchPolygon(entityType, entityId, propertyId).then((polygonFeature) => {
            if (!polygonFeature) return;
            const { features: current } = useMapStore.getState();
            if (!current) return;
            useMapStore.getState().setFeatures({
              type: "FeatureCollection",
              features: [...current.features, polygonFeature],
            });
            activatePolygon(entityId, isShift);
          });
          if (map.getZoom() < 14) {
            isProgrammaticMoveRef.current = true;
            map.easeTo({ center: [lng, lat], zoom: 15, duration: 800, easing: EASE_OUT_CUBIC });
          }
        } else if (map.getZoom() < 14) {
          isProgrammaticMoveRef.current = true;
          map.easeTo({ center: [lng, lat], zoom: 15, duration: 800, easing: EASE_OUT_CUBIC });
        }

        // Highlight buildings at the clicked feature location
        highlightBuildingsAt([lng, lat]);
        return;
      }

      // Clicked empty map — clear polygons + building highlights
      clearActivePolygons();
      clearBuildingHighlights();
      setPopupInfo(null);

      // Always set location tag on empty map click
      const { lng, lat } = e.lngLat;
      const lang = market === "NO" ? "no" : "sv";
      reverseGeocode(lng, lat, lang).then((name) => {
        setClickedLocation({ latitude: lat, longitude: lng, name });
      });
      setUserHasInteracted(true);
    },
    [setPopupInfo, setClickedLocation, setUserHasInteracted, activatePolygon, deactivatePolygon, clearActivePolygons, clearBuildingHighlights, highlightBuildingsAt, getPolygonBounds, isDrawing],
  );

  // ── Hover handlers ──
  const handleMouseEnter = useCallback(
    (e: MapLayerMouseEvent) => {
      const map = mapRef.current;
      if (!map || !e.features?.length) return;

      if (!isDrawing) setCursor("pointer");

      const feature = e.features[0]!;
      if (feature.layer?.id === "clusters") return; // No hover state on clusters

      const id = feature.id as number | undefined;
      const sourceId = feature.source as string ?? "results-points";
      if (id !== undefined) {
        hoveredFeatureRef.current = { source: sourceId, id };
        map.setFeatureState({ source: sourceId, id }, { hover: true });
      }
    },
    [isDrawing],
  );

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current;
    if (!isDrawing) setCursor("crosshair");

    if (hoveredFeatureRef.current && map) {
      map.setFeatureState(
        { source: hoveredFeatureRef.current.source, id: hoveredFeatureRef.current.id },
        { hover: false },
      );
      hoveredFeatureRef.current = null;
    }
  }, [isDrawing]);

  // ── Track bearing/pitch for compass (throttled to avoid re-renders every frame) ──
  const bearingRef = useRef(0);
  const pitchRef = useRef(0);
  const handleMove = useCallback((e: { viewState: { bearing: number; pitch: number } }) => {
    const b = Math.round(e.viewState.bearing);
    const p = Math.round(e.viewState.pitch);
    if (b !== bearingRef.current || p !== pitchRef.current) {
      bearingRef.current = b;
      pitchRef.current = p;
      setMapBearing(b);
      setMapPitch(p);
    }
  }, []);

  // ── Track user interaction (ignore programmatic moves) ──
  const handleMoveEnd = useCallback(() => {
    if (isProgrammaticMoveRef.current) {
      isProgrammaticMoveRef.current = false;
      return;
    }
    if (features && features.features.length > 0) {
      setUserHasInteracted(true);
    }
  }, [features, setUserHasInteracted]);

  // ── Result count (points only, not double-counting polygons or isochrones) ──
  const featureCount = splitFeatures.points.length;
  const hasIsochrones = splitFeatures.isochrones.length > 0;

  // ── Background feature count: staged-but-unrevealed (during turn) OR persisted background (after turn) ──
  const backgroundFeatureCount = useMemo(() => {
    // During analysis turn: count unrevealed staged features
    if (mapIntent?.mode === "analysis" && stagedFeatures) {
      return Math.max(0, stagedFeatures.features.length - revealedEntityIds.size);
    }
    // After turn: use persisted backgroundFeatures
    return backgroundFeatures?.features.length ?? 0;
  }, [mapIntent, stagedFeatures, revealedEntityIds, backgroundFeatures]);

  return (
    <div ref={containerRef} className={`cx-map-container relative h-full w-full${styleLoaded ? " cx-map-loaded" : ""}`}>
      <MapGL
        ref={mapRef}
        initialViewState={initialViewState}
        mapboxAccessToken={env.mapbox.accessToken}
        mapStyle={mapStyle}
        interactiveLayerIds={INTERACTIVE_LAYERS}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMoveEnd={handleMoveEnd}
        onMove={handleMove}
        dragRotate={is3D}
        cursor={isDrawing ? "crosshair" : cursor}
        onLoad={() => {
          setStyleLoaded(true);
          const rawMap = (mapRef.current as unknown as { getMap?: () => mapboxgl.Map })?.getMap?.();
          if (rawMap) {
            addStreetsSource(rawMap);
            useMapStore.getState().setMapInstance(rawMap);
            console.debug("[Ortvis] Streets v8 source added, map instance stored");
          }
        }}
        attributionControl={false}
        // @ts-expect-error mapboxgl v3 Standard style config, not yet in react-map-gl types
        config={isSatellite ? undefined : { basemap: { lightPreset: initialLightPreset } }}
        style={{ width: "100%", height: "100%" }}
      >
        {/* Custom map controls — top-right stack */}
        <div className="cx-map-controls">
          {/* Compass reset — only visible when rotated or tilted */}
          {(Math.abs(mapBearing) > 1 || mapPitch > 1) && (
            <button onClick={handleResetNorth} className="cx-map-controls__btn cx-map-compass animate-cx-fade-in" aria-label={t("map.resetNorth")} data-tooltip={t("map.resetNorth")}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="cx-map-compass__arrow" style={{ transform: `rotate(${-mapBearing}deg)` }}>
                <path d="M8 2L10.5 9H5.5L8 2Z" fill="var(--color-error-text)" />
                <path d="M8 14L5.5 7H10.5L8 14Z" fill="var(--color-text-secondary)" />
              </svg>
            </button>
          )}
          <button onClick={handleZoomIn} className="cx-map-controls__btn" aria-label={t("map.zoomIn")} data-tooltip={t("map.zoomIn")}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 3v10M3 8h10" /></svg>
          </button>
          <button onClick={handleZoomOut} className="cx-map-controls__btn" aria-label={t("map.zoomOut")} data-tooltip={t("map.zoomOut")}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 8h10" /></svg>
          </button>
          <button onClick={handleGeolocate} className="cx-map-controls__btn" aria-label={t("map.geolocate")} data-tooltip={t("map.geolocate")}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="8" cy="8" r="3" /><path d="M8 1v3M8 12v3M1 8h3M12 8h3" />
            </svg>
          </button>
          <button onClick={handleToggle3D} className={`cx-map-controls__btn ${mapPitch > 10 ? "cx-map-controls__btn--active" : ""}`} aria-label={t("map.toggle3d")} aria-pressed={mapPitch > 10} data-tooltip={t("map.toggle3d")}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
              <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" /><path d="M8 7.5V14M8 7.5L2 5.5M8 7.5L14 5.5" />
            </svg>
          </button>
          <button onClick={toggleMapStyle} className={`cx-map-controls__btn ${isSatellite ? "cx-map-controls__btn--active" : ""}`} aria-label={t(isSatellite ? "map.standard" : "map.satellite")} aria-pressed={isSatellite} data-tooltip={t(isSatellite ? "map.standard" : "map.satellite")}>
            {isSatellite ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round">
                <rect x="2" y="2" width="12" height="12" rx="1.5" /><path d="M2 6h12M2 10h12M6 2v12M10 2v12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                <circle cx="8" cy="8" r="6" /><ellipse cx="8" cy="8" rx="2.5" ry="6" /><path d="M2 8h12" />
              </svg>
            )}
          </button>
          <button onClick={() => setIsDrawing(!isDrawing)} className={`cx-map-controls__btn ${isDrawing ? "cx-map-controls__btn--active" : ""}`} aria-label={t("map.drawArea")} aria-pressed={isDrawing} data-tooltip={t("map.drawArea")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
            </svg>
          </button>
        </div>

        {/* Scale bar — bottom-left */}
        <ScaleControl position="bottom-left" unit="metric" />

        {/* Only render sources/layers after style is loaded (Mapbox Standard loads async) */}
        {styleLoaded && <>
          {/* Heatmap — density glow at low zoom, separate unclustered source */}
          {renderedPoints.features.length > 0 && (
            <Source id="heatmap-source" type="geojson" data={renderedPoints}>
              <Layer
                id="heatmap"
                type="heatmap"
                source="heatmap-source"
                maxzoom={13}
                paint={{
                  "heatmap-weight": 1,
                  "heatmap-intensity": [
                    "interpolate", ["linear"], ["zoom"],
                    4, 0.4,
                    8, 0.8,
                    12, 1.5,
                  ] as unknown as number,
                  "heatmap-color": heatmapGradient as unknown as string,
                  "heatmap-radius": [
                    "interpolate", ["linear"], ["zoom"],
                    4, 30,
                    7, 45,
                    10, 55,
                    12, 65,
                  ] as unknown as number,
                  "heatmap-opacity": HEATMAP_OPACITY_EXPR as unknown as number,
                }}
              />
            </Source>
          )}

          {/* Completed drawn polygon — rendered above basemap for clear visibility */}
          {drawnPolygonFC && (
            <Source id="drawn-polygon" type="geojson" data={drawnPolygonFC}>
              <Layer id="drawn-polygon-fill" type="fill" source="drawn-polygon"
                paint={{ "fill-color": entityColor, "fill-opacity": 0.12, "fill-emissive-strength": 1 }} />
              <Layer id="drawn-polygon-line" type="line" source="drawn-polygon"
                paint={{ "line-color": entityColor, "line-width": 2.5, "line-dasharray": [4, 3], "line-opacity": 0.85, "line-emissive-strength": 1 }} />
            </Source>
          )}

          {/* Isochrones rendered first (beneath entity polygons in same slot) */}
          {renderedIsochrones.features.length > 0 && (
            <Source id="results-isochrones" type="geojson" data={renderedIsochrones} generateId={true}>
              <Layer {...ISOCHRONE_FILL_LAYER} />
              <Layer {...ISOCHRONE_LINE_LAYER} />
            </Source>
          )}

          <Source id="results-polygons" type="geojson" data={renderedPolygons} generateId={true}>
            <Layer {...POLYGON_FILL_LAYER} />
            <Layer {...POLYGON_LINE_LAYER} />
          </Source>

          <Source
            id="results-points"
            type="geojson"
            data={renderedPoints}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
            generateId={true}
          >
            <Layer {...CLUSTER_LAYER} />
            <Layer {...CLUSTER_COUNT_LAYER} />
            <Layer {...UNCLUSTERED_POINT_LAYER} />
          </Source>
        </>}

        {/* Popup */}
        {popupInfo && (
          <MapPopup
            info={popupInfo}
            onClose={() => setPopupInfo(null)}
            onAskAbout={onAskAbout ?? (() => {})}
          />
        )}

        {/* Temporary location marker for area click */}
        {clickedLocation && (
          <Marker
            longitude={clickedLocation.longitude}
            latitude={clickedLocation.latitude}
            anchor="center"
          >
            <div className="cx-map-clicked-marker h-5 w-5 rounded-full shadow-md" />
          </Marker>
        )}

        {/* User location dot */}
        {userLocation && (
          <Marker longitude={userLocation[0]} latitude={userLocation[1]} anchor="center">
            <div className="cx-map-user-dot" />
          </Marker>
        )}
      </MapGL>

      {/* Status overlays — top-left flex stack */}
      <div className="cx-map-overlays">
        {isStreaming && (
          <div className="cx-map-overlay-badge animate-cx-fade-in">
            {t("map.loading")}
          </div>
        )}

        {featureCount > 0 && !isStreaming && (
          <div className="cx-map-overlay-count animate-cx-fade-in">
            {t("map.resultsCount", { count: featureCount })}
          </div>
        )}

        {userHasInteracted && featureCount > 0 && !isStreaming && (
          <button onClick={handleFitToResults} className="cx-map-clear-btn animate-cx-fade-in">
            {t("map.fitToResults")}
          </button>
        )}

        {pinnedPolygonIds.size > 0 && (
          <button onClick={clearActivePolygons} className="cx-map-clear-btn animate-cx-fade-in">
            {t("map.clearBoundaries")}
          </button>
        )}

        {hasIsochrones && !isStreaming && (
          <button onClick={clearIsochrones} className="cx-map-clear-btn animate-cx-fade-in">
            {t("map.clearIsochrone")}
          </button>
        )}

        {backgroundFeatureCount > 0 && !isStreaming && (
          <button
            className="cx-map-clear-btn animate-cx-fade-in"
            onClick={() => useMapStore.getState().toggleShowAll()}
            data-tooltip={showAllResults ? t("map.showCurated") : `${t("map.showAll")} ${backgroundFeatureCount} ${t("map.results")}`}
          >
            {showAllResults
              ? t("map.showCurated")
              : `${t("map.showAll")} ${backgroundFeatureCount} ${t("map.results")}`
            }
          </button>
        )}
      </div>

      {/* Draw mode hint — when drawing */}
      {isDrawing && (
        <div className="cx-map-draw-hint absolute left-1/2 bottom-8 -translate-x-1/2 rounded-lg px-4 py-2 shadow-sm animate-cx-fade-in">
          {t("map.drawHint")}
        </div>
      )}
    </div>
  );
}
